// src/pages/journalStock/hooks/useJournalStockData.js

import { useCallback, useEffect, useRef, useState } from 'react';
import { getJournalStock } from '../../../services/rapportService';
import { getMarques, getTailles } from '../../../services/marqueService';
import { messageErreur } from '../../../components/ndoe/chargement';

const DEFAULT_FILTERS = {
    date_debut: null,
    date_fin: null,
    marque_id: null,
    taille_id: null,
    couleur_id: null,
    categorie_id: null,
    search: '',
    showInactifs: 'dynamique',
    groupBy: 'variante',
    page: 1,
    limit: 25,
    sortBy: 'nom_produit',
    sortOrder: 'ASC'
};

export default function useJournalStockData() {
    const filtersRef = useRef(DEFAULT_FILTERS);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [data, setData] = useState([]);
    // UNE PANNE N'EST PAS UN STOCK VIDE (10/08/2026).
    //
    // Le catch remplaçait toute erreur par `setData([])`, et l'écran affichait
    // alors « Aucun article — Ajustez la recherche ou les filtres ». La
    // propriétaire pouvait donc croire son stock vide alors que le serveur
    // n'avait simplement pas répondu. C'est le pire des cas de cette famille :
    // il porte sur la marchandise.
    const [erreur, setErreur] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25, total: 0 });
    
    const [marque, setMarque] = useState([]);
    const [pointure, setPointure] = useState([]);
    const [marqueLoading, setMarqueLoading] = useState(false);
    
    // ✅ Ajouter un état de version pour forcer le rafraîchissement
    const [version, setVersion] = useState(0);

    const loadReferences = useCallback(async () => {
        setMarqueLoading(true);
        try {
            const [marqueData, tailleData] = await Promise.all([
                getMarques(),
                getTailles()
            ]);
            setMarque(marqueData?.data || marqueData || []);
            setPointure(tailleData?.data || tailleData || []);
        } catch (error) {
            console.error('❌ Erreur chargement références:', error);
            setMarque([]);
            setPointure([]);
        } finally {
            setMarqueLoading(false);
        }
    }, []);

    const fetchDatas = useCallback(async (patch = {}) => {
        const next = { ...filtersRef.current, ...patch };
        
        // ✅ Nettoyer les paramètres
        const cleanParams = {};
        Object.keys(next).forEach(key => {
            const value = next[key];
            if (value !== null && value !== undefined && value !== '') {
                if (Array.isArray(value)) {
                    cleanParams[key] = value.join(',');
                } else {
                    cleanParams[key] = value;
                }
            }
        });
        
        filtersRef.current = cleanParams;
        setFilters(cleanParams);
        setLoading(true);

        try {
            console.log('📤 Envoi des paramètres:', cleanParams);
            const response = await getJournalStock(cleanParams);
            
            const responseData = response.data;
            let dataArray = [];
            let paginationData = {};
            
            if (Array.isArray(responseData)) {
                dataArray = responseData;
                paginationData = {
                    current: 1,
                    limit: 25,
                    total: responseData.length
                };
            } else if (responseData && typeof responseData === 'object') {
                if (responseData.data && Array.isArray(responseData.data)) {
                    dataArray = responseData.data;
                    paginationData = responseData.pagination || paginationData;
                } else {
                    dataArray = [];
                }
            }
            
            console.log('📊 Nouvelles données:', dataArray);
            console.log('📊 Nombre:', dataArray.length);
            
            // ✅ Mettre à jour les données
            setData(dataArray);
            setPagination({
                current: paginationData.current || 1,
                pageSize: paginationData.pageSize || 25,
                total: paginationData.total || dataArray.length || 0
            });
            
            // ✅ Incrémenter la version pour forcer le rafraîchissement
            setVersion(prev => prev + 1);
            setErreur(null);   // le chargement a réussi : la panne précédente est levée
            
            return responseData;
        } catch (error) {
            console.error('❌ Erreur fetchDatas:', error);
            // On GARDE les dernières données affichées : mieux vaut un journal un
            // peu ancien, signalé comme tel, qu'un écran vide qui ment.
            setErreur(messageErreur(error, 'le journal de stock'));
        } finally {
            setLoading(false);
        }
    }, []);

    const refreshWithFilters = useCallback((patch = {}) =>
        fetchDatas({ ...patch, page: 1 }),
    [fetchDatas]);

    const handleTableChange = useCallback((pageConfig, _filters, sorter) =>
        fetchDatas({
            page: pageConfig.current || 1,
            limit: pageConfig.pageSize || filtersRef.current.limit,
            sortBy: sorter?.field || filtersRef.current.sortBy,
            sortOrder: sorter?.order === 'ascend' ? 'ASC' : 'DESC'
        }),
    [fetchDatas]);

    useEffect(() => {
        loadReferences();
        fetchDatas();
    }, [loadReferences, fetchDatas]);

    return {
        data,
        erreur,
        loading: loading || marqueLoading,
        pagination,
        filters,
        marque,
        pointure,
        version, // ✅ Exporter la version
        fetchDatas,
        refreshWithFilters,
        handleTableChange
    };
}